import classes from './NotificationAlertOverlay.module.scss';
import { NotificationState } from '@webinex/wispo';
import { Button, Col, List, Row, Tooltip, Typography } from 'antd';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useAppWispoList, useAppWispoUnreadCount, Notification } from '@app/notifications/api';
import classNames from 'classnames';
import dayjs from 'dayjs';
import { formatters, Icon } from '@app/ui';
import { useBool } from '@app/base';
import { FilterRule, PagingRule, SortRule } from '@webinex/asky';
import { useLocalizedNotification } from 'packages/notifications/utils';

interface Props {
  onOpenSettings: () => void;
}

const { Title, Text } = Typography;

function ListItem({
  item,
  onMarkRead,
}: {
  item: NotificationState<Notification>;
  onMarkRead: (id: string) => any;
}) {
  const { id } = item;
  const handleRead = useCallback(() => onMarkRead(id), [id, onMarkRead]);
  const { t } = useTranslation();
  const markRead = item.markRead;
  const busy = markRead.isPending || markRead.isReloading;

  const { subject, body, createdAt } = useLocalizedNotification(item);

  return (
    <div className="py-4">
      <Row wrap={false} className="mb-1">
        <Col flex="auto">
          <Tooltip overlay={subject}>
            <Text className={classes.title} ellipsis strong>
              {subject}
            </Text>
          </Tooltip>
          <Tooltip className="ms-3" overlay={formatters.datetime(createdAt)}>
            <Text type="secondary">{dayjs.tz(createdAt).fromNow()}</Text>
          </Tooltip>
        </Col>
        <Col flex="none">
          <Button type="link" loading={busy} className="text-small" onClick={handleRead}>
            {!busy && t('notifications.alert.markRead')}
            {busy && t('notifications.alert.markingRead')}
          </Button>
        </Col>
      </Row>
      <div className="text-multiline">{body}</div>
    </div>
  );
}

const FILTER: FilterRule = {
  operator: '=',
  fieldId: 'isRead',
  value: false,
};

const SORT: SortRule[] = [{ fieldId: 'createdAt', dir: 'desc' }];
const PROPS = ['items', 'totalUnreadCount'];
const PAGING: PagingRule = { skip: 0, take: 10 };

export function NotificationAlertOverlay({ onOpenSettings }: Props) {
  const { t } = useTranslation(undefined, { keyPrefix: 'notifications.alert' });
  const unread = useAppWispoUnreadCount();
  const push = useNavigate();
  const [markViewReadPending, setMarkViewReadPending] = useBool(false);

  const { isPending, items, read, readView } = useAppWispoList({
    pagingRule: PAGING,
    filterRule: FILTER,
    sortRule: SORT,
    props: PROPS,
  });

  const handleMarkRead = (id: string) => {
    read([id]);
  };

  const handleMarkViewRead = () => {
    setMarkViewReadPending(true);
    readView()
      .unwrap()
      .finally(() => setMarkViewReadPending(false));
  };

  return (
    <div className={classes.container}>
      <Row gutter={20} wrap={false} align="middle" className="mb-5">
        <Col flex="auto">
          <Title className="my-0 inline" level={5}>
            {t('title')}
          </Title>
          <Text type="secondary" className={classNames(classes.count, 'ms-4')}>
            {t('unread', { count: unread })}
          </Text>
        </Col>
        <Col flex="none">
          <Button
            type="link"
            icon={<Icon type="settings" />}
            onClick={onOpenSettings}
            aria-label={t('notifications.openSettings')}
          />
        </Col>
      </Row>

      <List
        className={classes.list}
        dataSource={items}
        loading={isPending}
        renderItem={(item: NotificationState<Notification>) => (
          <ListItem item={item} onMarkRead={handleMarkRead} />
        )}
      />

      <Row justify="space-between">
        <Button type="link" onClick={() => push(`/notifications`)}>
          {t('showAll')}
        </Button>
        <Button type="link" loading={markViewReadPending} className="text-small" onClick={handleMarkViewRead}>
          {markViewReadPending ? t('markingViewRead') : t('markViewRead')}
        </Button>
      </Row>
    </div>
  );
}
